import { useCallback, useEffect, useState } from "react";
import { apiGet, apiPut } from "../../../api/http";
import { getTenantLegacyId } from "../../../api/tenantContext";
import { getUserFacingMessage } from "../../../api/errors";
import { isActivityRegClosed } from "../../../utils/activityRegistrationWindow";

type LinkedStats = { quota: number; enrolled: number; full: boolean };
type PointsOverride = {
  configured: boolean;
  participationPoints: number;
  enableParticipationPointDeduction: boolean;
  notRegisteredDeductionPoints: number;
  notCheckedInDeductionPoints: number;
} | null;

type LinkedActivityRow = {
  activity: {
    id: string;
    title: string;
    date: string;
    location?: string;
    status: string;
    regEndDate?: string;
    ownerTenantId?: string;
    ownerTenantName?: string;
  };
  linkedStats: LinkedStats;
  pointsOverride: PointsOverride;
};

type RegRow = {
  id: string;
  name: string;
  phone: string;
  level: string;
  applyDate: string;
  memberLegacyId?: string | null;
  checkedIn: boolean;
};

export default function LinkedActivityManager() {
  const tid = getTenantLegacyId();
  const [rows, setRows] = useState<LinkedActivityRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LinkedActivityRow | null>(null);
  const [regs, setRegs] = useState<RegRow[]>([]);
  const [pointsForm, setPointsForm] = useState({
    participationPoints: 0,
    enableParticipationPointDeduction: false,
    notRegisteredDeductionPoints: 0,
    notCheckedInDeductionPoints: 0,
  });
  const [savingPoints, setSavingPoints] = useState(false);

  const load = useCallback(async () => {
    if (!tid) return;
    setBusy(true);
    setError(null);
    try {
      const data = await apiGet<LinkedActivityRow[]>(
        `/api/tenants/${encodeURIComponent(tid)}/linked-activities`,
      );
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(getUserFacingMessage(e, "加载关联活动失败"));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (row: LinkedActivityRow) => {
    setSelected(row);
    const ov = row.pointsOverride;
    setPointsForm({
      participationPoints: ov?.participationPoints ?? 0,
      enableParticipationPointDeduction: ov?.enableParticipationPointDeduction ?? false,
      notRegisteredDeductionPoints: ov?.notRegisteredDeductionPoints ?? 0,
      notCheckedInDeductionPoints: ov?.notCheckedInDeductionPoints ?? 0,
    });
    setRegs([]);
    const owner = row.activity.ownerTenantId;
    if (!owner || !tid) return;
    try {
      const data = await apiGet<{ registrations?: RegRow[] }>(
        `/api/tenants/${encodeURIComponent(tid)}/linked-activities/${encodeURIComponent(owner)}/${encodeURIComponent(row.activity.id)}/registrations`,
      );
      setRegs(Array.isArray(data?.registrations) ? data.registrations : []);
    } catch {
      setRegs([]);
    }
  };

  const savePoints = async () => {
    if (!selected?.activity.ownerTenantId || !tid) return;
    if (isActivityRegClosed(selected.activity.regEndDate ?? "")) {
      setError("报名已截止，不可再修改积分规则");
      return;
    }
    setSavingPoints(true);
    setError(null);
    try {
      await apiPut(
        `/api/tenants/${encodeURIComponent(tid)}/linked-activities/${encodeURIComponent(selected.activity.ownerTenantId)}/${encodeURIComponent(selected.activity.id)}/points-override`,
        pointsForm,
      );
      await load();
      setSelected((prev) =>
        prev
          ? {
              ...prev,
              pointsOverride: { configured: true, ...pointsForm },
            }
          : prev,
      );
    } catch (e) {
      setError(getUserFacingMessage(e, "保存积分配置失败"));
    } finally {
      setSavingPoints(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">关联活动</h2>
          <p className="mt-1 text-sm text-gray-500">
            发布方授权本协会报名的活动。可配置本协会积分（未配置则加扣均为 0），并查看本协会报名/签到。
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={busy}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          刷新
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      {busy && rows.length === 0 ? (
        <p className="text-sm text-gray-400">加载中…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-400">
          暂无关联活动。当上下级协会在创建活动时勾选本协会并设名额后，会出现在这里。
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">活动</th>
                <th className="px-4 py-2.5 font-medium">发布协会</th>
                <th className="px-4 py-2.5 font-medium">本协会名额</th>
                <th className="px-4 py-2.5 font-medium">积分覆盖</th>
                <th className="px-4 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.activity.ownerTenantId}-${row.activity.id}`} className="border-t border-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{row.activity.title}</div>
                    <div className="text-xs text-gray-400">
                      {row.activity.date}
                      {row.activity.location ? ` · ${row.activity.location}` : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{row.activity.ownerTenantName || row.activity.ownerTenantId}</td>
                  <td className="px-4 py-3">
                    {row.linkedStats.enrolled}/{row.linkedStats.quota}
                    {row.linkedStats.full ? (
                      <span className="ml-1 text-xs text-amber-600">已满</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {row.pointsOverride?.configured
                      ? `参与 +${row.pointsOverride.participationPoints}`
                      : "未配置（加扣为 0）"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void openDetail(row)}
                      className="text-sm text-amber-700 hover:text-amber-800"
                    >
                      管理
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{selected.activity.title}</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  发布：{selected.activity.ownerTenantName} · 名额{" "}
                  {selected.linkedStats.enrolled}/{selected.linkedStats.quota}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg px-2 py-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="mb-5 space-y-3 rounded-xl border border-gray-100 bg-gray-50/80 p-4">
              <p className="text-sm font-medium text-gray-800">本协会积分规则</p>
              <p className="text-[11px] text-gray-400">
                {isActivityRegClosed(selected.activity.regEndDate ?? "")
                  ? "报名已截止，积分规则不可再修改。"
                  : "未保存过配置时，本协会会员参与加分与扣分均为 0。"}
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                参与加分
                <input
                  type="number"
                  min={0}
                  disabled={isActivityRegClosed(selected.activity.regEndDate ?? "")}
                  value={pointsForm.participationPoints}
                  onChange={(e) =>
                    setPointsForm((p) => ({
                      ...p,
                      participationPoints: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                    }))
                  }
                  className="w-24 rounded-lg border border-gray-200 px-2 py-1 disabled:bg-gray-100"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  disabled={isActivityRegClosed(selected.activity.regEndDate ?? "")}
                  checked={pointsForm.enableParticipationPointDeduction}
                  onChange={(e) =>
                    setPointsForm((p) => ({
                      ...p,
                      enableParticipationPointDeduction: e.target.checked,
                    }))
                  }
                />
                启用扣分
              </label>
              {pointsForm.enableParticipationPointDeduction ? (
                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  <label className="flex items-center gap-2">
                    未报名扣
                    <input
                      type="number"
                      min={0}
                      disabled={isActivityRegClosed(selected.activity.regEndDate ?? "")}
                      value={pointsForm.notRegisteredDeductionPoints}
                      onChange={(e) =>
                        setPointsForm((p) => ({
                          ...p,
                          notRegisteredDeductionPoints: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                        }))
                      }
                      className="w-20 rounded-lg border border-gray-200 px-2 py-1 disabled:bg-gray-100"
                    />
                  </label>
                  <label className="flex items-center gap-2">
                    未签到扣
                    <input
                      type="number"
                      min={0}
                      disabled={isActivityRegClosed(selected.activity.regEndDate ?? "")}
                      value={pointsForm.notCheckedInDeductionPoints}
                      onChange={(e) =>
                        setPointsForm((p) => ({
                          ...p,
                          notCheckedInDeductionPoints: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                        }))
                      }
                      className="w-20 rounded-lg border border-gray-200 px-2 py-1 disabled:bg-gray-100"
                    />
                  </label>
                </div>
              ) : null}
              {!isActivityRegClosed(selected.activity.regEndDate ?? "") ? (
              <button
                type="button"
                disabled={savingPoints}
                onClick={() => void savePoints()}
                className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
              >
                {savingPoints ? "保存中…" : "保存积分配置"}
              </button>
              ) : null}
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-gray-800">本协会报名名单</p>
              {regs.length === 0 ? (
                <p className="text-sm text-gray-400">暂无本协会报名</p>
              ) : (
                <ul className="divide-y divide-gray-50 rounded-xl border border-gray-100">
                  {regs.map((r) => (
                    <li key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span>
                        {r.name} · {r.phone}
                        <span className="ml-2 text-xs text-gray-400">{r.applyDate}</span>
                      </span>
                      <span className={`text-xs ${r.checkedIn ? "text-teal-600" : "text-gray-400"}`}>
                        {r.checkedIn ? "已签到" : "未签到"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
